import React, { useState } from "react";
import { Lock, Mail, Eye, EyeOff, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "./AuthContext";
import { useBranding } from "./BrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const { login } = useAuth();
  const branding = useBranding();
  const [view, setView] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pre-fill token from URL if present (mount-only)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) {
      setToken(t);
      setView("reset");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess("If your email exists, a reset link has been sent. Check your inbox.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccess("Password reset successfully! You can now login.");
      setTimeout(() => {
        setView("login");
        setToken("");
        setNewPassword("");
        window.history.replaceState(null, "", window.location.pathname);
      }, 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950 flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Title */}
        <motion.div
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="text-center mb-8"
        >
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-[0.25em] text-amber-400 drop-shadow-[0_0_20px_rgba(251,191,36,0.35)]"
            data-testid="login-club-title"
          >
            {branding.club_name}
          </h1>
          <p className="text-xs sm:text-sm text-amber-200/70 mt-2 tracking-widest">MEMBER MANAGEMENT</p>
        </motion.div>

        <div className="bg-white rounded-3xl shadow-2xl border border-amber-100 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              {view === "login" && "Welcome Back"}
              {view === "forgot" && "Forgot Password?"}
              {view === "reset" && "Reset Password"}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              {view === "login" && `Sign in to ${branding.club_name} Admin`}
              {view === "forgot" && "Enter admin email to receive a reset link. Staff must contact admin for password changes."}
              {view === "reset" && "Enter your new password below"}
            </p>
          </div>

          {/* Error/Success messages */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 flex items-start gap-2"
              data-testid="auth-error"
            >
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-xl bg-green-50 border border-green-100"
              data-testid="auth-success"
            >
              <p className="text-sm text-green-700">{success}</p>
            </motion.div>
          )}

          {/* Login Form */}
          {view === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-900">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10 h-12 rounded-xl bg-slate-50 focus:bg-white"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-900">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pl-10 pr-10 h-12 rounded-xl bg-slate-50 focus:bg-white"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium"
                data-testid="login-submit-button"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
              <button
                type="button"
                onClick={() => { setView("forgot"); setError(""); setSuccess(""); }}
                className="w-full text-sm text-amber-600 hover:text-amber-700 mt-2"
                data-testid="forgot-password-link"
              >
                Forgot password? (admin only)
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {view === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-900">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10 h-12 rounded-xl bg-slate-50 focus:bg-white"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium"
                data-testid="forgot-submit-button"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <button
                type="button"
                onClick={() => { setView("login"); setError(""); setSuccess(""); }}
                className="w-full text-sm text-amber-600 hover:text-amber-700"
                data-testid="back-to-login-link"
              >
                Back to login
              </button>
              <button
                type="button"
                onClick={() => { setView("reset"); setError(""); setSuccess(""); }}
                className="w-full text-xs text-slate-500 hover:text-slate-700"
                data-testid="have-token-link"
              >
                I already have a reset token
              </button>
            </form>
          )}

          {/* Reset Password Form */}
          {view === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="token" className="text-sm font-medium text-slate-900">Reset Token</Label>
                <Input
                  id="token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your reset token"
                  required
                  className="mt-1.5 h-12 rounded-xl bg-slate-50 focus:bg-white font-mono text-xs"
                  data-testid="reset-token-input"
                />
              </div>
              <div>
                <Label htmlFor="new-password" className="text-sm font-medium text-slate-900">New Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                    className="pl-10 pr-10 h-12 rounded-xl bg-slate-50 focus:bg-white"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium"
                data-testid="reset-submit-button"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
              <button
                type="button"
                onClick={() => { setView("login"); setError(""); setSuccess(""); }}
                className="w-full text-sm text-amber-600 hover:text-amber-700"
              >
                Back to login
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-amber-200/60 mt-6">{branding.club_name} Member Management</p>
      </motion.div>
    </div>
  );
}
